import {Key, useEffect, useState} from 'react';
import {wdiCore, WdiInput, WdiModalAppProps} from '@/components/WdiControls';
import {EC} from '@/services/mdm/ec';
import {RebateInfo} from '@/services/sales/rebateinfo';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ButtonColumn, FilterColumn, GridFilterForm} from '@/components/GridWidget/GridFilterForm';
import {DatePicker, message} from 'antd';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {WdiSelect} from '@/components/WdiControls/WdiSelect';
import {SorterResult} from 'antd/lib/table/interface';
import {Enum} from '@/enums/system';
import moment from 'moment';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {ResponseStatus} from '@/utils/request';
import {toExcel} from '@/components/WdiControls/WdiExcel';
import {SoldTo} from '@/services/mdm/soldto';
import {ProductPolicy} from '@/services/policy/productpolicy';

const {RangePicker} = DatePicker;

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any,
}

const columns: ColumnsType<any> = [
  {
    title: '返利单编号',
    width: 80,
    dataIndex: 'rebateInfoId',
    ellipsis: true,
  },
  {
    title: '系统外政策/合同',
    width: 110,
    dataIndex: 'extPolicyName',
    ellipsis: true,
  },
  {
    title: '客户',
    width: 120,
    dataIndex: 'soldToCode',
    ellipsis: true,
    render: (_, record) => wdiCore.String.CodeOrName(record.soldToCode, record.soldToName),
  },
  {
    title: '费用项',
    width: 100,
    dataIndex: 'ecId',
    ellipsis: true,
    render: (_, record) => record.ecName,
  },
  {
    title: '销售开始日期',
    width: 80,
    dataIndex: 'startDate',
    ellipsis: true,
  },
  {
    title: '销售截止日期',
    width: 80,
    dataIndex: 'endDate',
    ellipsis: true,
  },
  {
    title: '产品别',
    dataIndex: 'productNamesRebate',
    width: 100,
    ellipsis: true,
  },
  {
    title: '产品别编号',
    dataIndex: 'productCodesRebate',
    width: 100,
    ellipsis: true,
  },
  {
    title: '结案金额',
    width: 80,
    dataIndex: 'amountSs',
    ellipsis: true,
  },
  {
    title: '可核销金额',
    width: 80,
    dataIndex: 'amountNotPaid',
    ellipsis: true,
  },
];

const RebateInfoSelectApp: React.FC<WdiModalAppProps> = (props) => {
  const {data} = props;

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
    },
    sorter: null,
    filterOptions: null,
  });
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const buttonColumns: ButtonColumn[] = [
    {
      title: '导出',
      key: 'Export',
      doClick: (filterOptions: any) => {
        let columns: any[] = [
          {name: 'rebateInfoId', displayName: '返利单编号*'},
          {name: 'extPolicyName', displayName: '系统外政策/合同'},
          {name: 'soldToCode', displayName: '客户编号'},
          {name: 'soldToName', displayName: '客户名称'},
          {name: 'shipToCode', displayName: '收货地编号（用户填写，票扣时需填写）'},
          {name: 'productNamesRebate', displayName: '产品别'},
          {name: 'productCodesRebate', displayName: '产品别编号'},
          {name: 'ecName', displayName: '费用项'},
          {name: 'startDate', displayName: '销售开始日期'},
          {name: 'endDate', displayName: '销售截止日期'},
          {
            name: 'fperiod',
            displayName: '预提月份',
            type: Enum.FieldType.NUMBER,
            dataType: Enum.FieldDataType.INT,
          },
          {name: 'amountSs', displayName: '结案金额'},
          {name: 'amountNotPaid', displayName: '可核销金额'},
          {name: 'amountInVat', displayName: '核销金额(含税)*（用户填写）'},
          {
            name: 'amountExVat',
            displayName: '核销金额(不含税)（账扣/转账/第三方时可填，不填则自动计算）',
          },
          {name: 'remark', displayName: '核销备注（用户填写）'},
          {name: 'amountPaid', displayName: '已核销金额'},
          {name: 'formulaRebateParameters', displayName: '参数计算值'},
          {name: 'policyCode', displayName: '政策编号'},
          {name: 'policyName', displayName: '政策名称'},
          {name: 'paymentMethodName', displayName: '支付方式'},
        ];
        const req: API.QueryForPageReq = {
          filterOptions: JSON.stringify(
            getFilterOptions(Object.assign({}, tableParams, {filterOptions})),
          ),
          pagenum: 0,
          pagesize: 0,
        };
        RebateInfo.queryForSelect(req).then((response) => {
          const sheet = {
            data: response?.data?.records,
            names: columns,
            sheetName: 'RebateInfo',
            cells: {
              soldToCodePay: (fieldObject: any, row: any, value: any) => {
                return row.soldToCode;
              },
            },
          };
          toExcel({
            name: 'RebateInfo_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
            sheetProps: [sheet],
          });
        });
      },
    },
  ];
  const filterColumns: FilterColumn[] = [
    {
      title: '客户',
      dataIndex: 'soldToCode',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              allowClear: true,
              placeholder: '请选择客户',
              showSearch: true,
              remote: true,
              mode: 'multiple',
              dataLoading: async (params) => {
                const response = await SoldTo.ajaxSearch(Object.assign({}, params));
                return response.data.map((item) => {
                  return {value: item.soldToCode, label: item.codeOrName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '销售日期',
      dataIndex: 'startDate',
      span: 8,
      render: () => {
        return <RangePicker style={{width: '100%'}}/>;
      },
    },
    {
      title: '费用项',
      dataIndex: 'ecId',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              mode: 'multiple',
              allowClear: true,
              placeholder: '请选择费用项',
              showSearch: true,
              dataLoading: async (params) => {
                const response = await EC.searchEc3(
                  Object.assign(
                    {
                      pageTypeIds: [
                        Enum.PageType.CONTRACT_REBATE,
                        Enum.PageType.POLICY_REBATE,
                      ].join(),
                    },
                    params,
                  ),
                );

                return response.data.map((item) => {
                  return {value: item.ecId, label: item.ecName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '政策',
      dataIndex: 'productPolicyId',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              placeholder: '请选择政策',
              showSearch: true,
              allowClear: true,
              mode: 'multiple',
              remote: true,
              dataLoading: async (params) => {
                const response = await ProductPolicy.ajaxSearch(params);
                return response.data.map((item) => {
                  return {value: item.productPolicyId, label: item.codeOrName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '系统外政策/合同',
      dataIndex: 'extPolicyName',
      span: 4,
      render: () => {
        return <WdiInput/>;
      },
    },
    {
      title: '返利单号',
      dataIndex: 'rebateInfoId',
      span: 4,
      render: () => {
        return <WdiInput/>;
      },
    },
  ];
  const arFilterColumns: FilterColumn[] = [
    {
      title: '销售日期',
      dataIndex: 'startDate',
      span: 8,
      render: () => {
        return <RangePicker style={{width: '100%'}}/>;
      },
    },
    {
      title: '费用项',
      dataIndex: 'ecId',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              mode: 'multiple',
              allowClear: true,
              placeholder: '请选择费用项',
              showSearch: true,
              dataLoading: async (params) => {
                const response = await EC.searchEc3(
                  Object.assign(
                    {
                      pageTypeIds: [
                        Enum.PageType.CONTRACT_REBATE,
                        Enum.PageType.POLICY_REBATE,
                      ].join(),
                    },
                    params,
                  ),
                );

                return response.data.map((item) => {
                  return {value: item.ecId, label: item.ecName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '政策',
      dataIndex: 'productPolicyId',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              placeholder: '请选择政策',
              showSearch: true,
              allowClear: true,
              mode: 'multiple',
              remote: true,
              dataLoading: async (params) => {
                const response = await ProductPolicy.ajaxSearch(params);
                return response.data.map((item) => {
                  return {value: item.productPolicyId, label: item.codeOrName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '系统外政策/合同',
      dataIndex: 'extPolicyName',
      span: 4,
      render: () => {
        return <WdiInput/>;
      },
    },
    {
      title: '返利单号',
      dataIndex: 'rebateInfoId',
      span: 4,
      render: () => {
        return <WdiInput/>;
      },
    },
  ];

  useEffect(() => {
    fetchData(Object.assign({}, tableParams));
  }, []);

  const getFilterOptions = (params: TableParams) => {
    const filterOptions: any = {
      paymentMethodFinalId: data.paymentMethodId,
      soldToCode: data.soldToCode ? [data.soldToCode] : undefined,
      companyCode: data.companyCode,
    };
    if (props.data.dataSource && props.data.dataSource.length > 0) {
      filterOptions.excludeRebateInfoIds = props.data.dataSource.map((item: any) => item.rebateInfoId).join()
    }

    if (params.filterOptions) {
      const {startDate, ecId, soldToCode, productPolicyId, extPolicyName,rebateInfoId} = params.filterOptions;
      if (!wdiCore.Object.IsNullOrUndefined(startDate)) {
        filterOptions.startDate = startDate
          .map((item: any) => {
            return moment(item).format('YYYY-MM-DD');
          })
          .join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(ecId) && ecId.length > 0) {
        filterOptions.ecId = ecId.join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(soldToCode) && soldToCode.length > 0) {
        filterOptions.soldToCode = soldToCode;
      }
      if (!wdiCore.Object.IsNullOrUndefined(extPolicyName) && extPolicyName.length > 0) {
        filterOptions.extPolicyName = extPolicyName;
      }
      if (!wdiCore.Object.IsNullOrUndefined(productPolicyId) && productPolicyId.length > 0) {
        filterOptions.productPolicyId = productPolicyId.join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(rebateInfoId) && rebateInfoId.length > 0) {
        filterOptions.rebateInfoId = rebateInfoId;
      }
    }
    return filterOptions;
  };

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: API.QueryForPageReq = {
      filterOptions: JSON.stringify(filterOptions),
      pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
    };
    RebateInfo.queryForSelect(req).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data.records);
        setTableParams({
          pagination: {
            ...params.pagination,
            total: data.totalCount,
          },
          sorter: params.sorter,
          filterOptions: params.filterOptions
        });
        setSelectedRowKeys([]);
      }
      setLoading(false);
    });
  };
  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  }

  const onSelectChange = (newSelectedRowKeys: Key[], newSelectedRows: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };
  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      onCancel={() => {
        props.onClose ? props.onClose() : null;
      }}
      onOk={() => {
        if (selectedRows.length == 0) {
          message.error('请至少选择一条数据');
        } else {
          selectedRows.forEach((item: any) => {
            item.paymentMethodId = data.paymentMethodId;
            item.vatTypeId = data.vatType?.vatTypeId;
            item.vatTypeCode = data.vatType?.vatTypeCode;
            item.vatTypeName = data.vatType?.vatTypeName;
            item.vatTypeValue = data.vatType?.vatTypeValue;
          });
          props.onSave?.({
            code: ResponseStatus.SUCCESS,
            data: selectedRows,
          });
        }
      }}
    >
      <GridFilterForm
        doSearch={doSearch}
        columns={data.soldToCode ? arFilterColumns : filterColumns}
        buttonColumns={buttonColumns}
      />
      <WdiTable
        showPageRows={10}
        rowSelection={{selectedRowKeys, onChange: onSelectChange}}
        columns={columns}
        size="small"
        dataSource={dataSource}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(
              Object.assign({}, tableParams, {
                pagination: {
                  ...tableParams.pagination,
                  current: page,
                  pageSize: pageSize,
                },
              }),
            );
          },
        }}
        loading={loading}
      />
    </WdiModal>
  );
};
export default RebateInfoSelectApp;
